/**
 * <rs-load-more>: the "Load 40 more" button, the progress bar and the "next set" link under the collection grid.
 * Rendered by snippets/rs-load-more.liquid inside <results-list manual-load>. The batches themselves are fetched and
 * inserted by Horizon's paginated list (assets/paginated-list.js, manual-load mode); this element only drives it and
 * keeps the count, the meter and the button / next-set link in step with what is on the page.
 *
 * A "set" is N batches (default 5 x 40 = 200 products). After the last batch of a set the button gives way to a link
 * that opens the next set as its own page, so one page never grows past 200 products.
 */
class RsLoadMore extends HTMLElement {
  /** @type {MutationObserver | undefined} */
  #gridObserver;
  #busy = false;

  connectedCallback() {
    this.querySelector('[data-rs-more-button]')?.addEventListener('click', this.#onClick);
    document.addEventListener('rs:products-loaded', this.#update);

    // Filters and sorting re-render the grid, so watch it as well.
    const grid = this.#grid;
    if (grid) {
      this.#gridObserver = new MutationObserver(this.#update);
      this.#gridObserver.observe(grid, { childList: true });
    }
    this.#update();
  }

  disconnectedCallback() {
    document.removeEventListener('rs:products-loaded', this.#update);
    this.#gridObserver?.disconnect();
  }

  get #list() {
    const list = this.closest('results-list');
    return /** @type {(HTMLElement & { loadMore?: () => Promise<void>, refs?: { grid?: HTMLElement } }) | null} */ (list);
  }

  get #grid() {
    return this.#list?.refs?.grid ?? this.closest('results-list')?.querySelector('[ref="grid"]') ?? null;
  }

  #onClick = async () => {
    const list = this.#list;
    const button = this.querySelector('[data-rs-more-button]');
    if (!list?.loadMore || this.#busy || !(button instanceof HTMLButtonElement)) return;

    this.#busy = true;
    button.disabled = true;
    button.classList.add('is-loading');
    try {
      await list.loadMore();
    } finally {
      this.#busy = false;
      button.disabled = false;
      button.classList.remove('is-loading');
      this.#update();
    }
  };

  #update = () => {
    const grid = this.#grid;
    if (!grid) return;

    const cards = [...grid.querySelectorAll(':scope > [data-page]')];
    if (!cards.length) return;

    const pages = cards.map((card) => Number(card instanceof HTMLElement ? card.dataset.page : 0));
    const firstLoaded = Math.min(...pages);
    const lastLoaded = Math.max(...pages);
    const lastPage = Number(grid.dataset.lastPage) || lastLoaded;
    const perLoad = Number(this.dataset.perLoad) || 40;
    const total = Number(this.dataset.total) || cards.length;
    const setSize = Math.max(1, Number(this.dataset.setSize) || 5);

    const setStart = Math.floor((firstLoaded - 1) / setSize) * setSize + 1;
    const setEnd = setStart + setSize - 1;
    const lastInSet = Math.min(setEnd, lastPage);
    const hasMoreInSet = lastLoaded < lastInSet;
    const hasNextSet = lastLoaded >= setEnd && lastLoaded < lastPage;

    const from = (firstLoaded - 1) * perLoad + 1;
    const to = (firstLoaded - 1) * perLoad + cards.length;
    const count = this.querySelector('[data-rs-more-count]');
    if (count) count.textContent = lastLoaded >= lastPage ? `That’s everything: ${total} charms` : `Showing ${from}–${to} of ${total}`;

    const meter = this.querySelector('.rs-more__meter i');
    if (meter instanceof HTMLElement) meter.style.width = `${((lastLoaded - setStart + 1) / (lastInSet - setStart + 1)) * 100}%`;

    const button = this.querySelector('[data-rs-more-button]');
    if (button instanceof HTMLButtonElement) {
      button.hidden = !hasMoreInSet;
      button.textContent = `Load ${Math.min(perLoad, total - to)} more`;
    }

    const next = this.querySelector('[data-rs-more-next]');
    if (next instanceof HTMLAnchorElement) {
      next.hidden = !hasNextSet;
      const url = new URL(location.href);
      url.searchParams.set('page', String(setEnd + 1));
      url.hash = '';
      next.href = url.toString();
    }

    const prev = this.querySelector('[data-rs-more-prev]');
    if (prev instanceof HTMLAnchorElement) {
      prev.hidden = setStart <= 1;
      const url = new URL(location.href);
      url.searchParams.set('page', String(Math.max(1, setStart - setSize)));
      url.hash = '';
      prev.href = url.toString();
    }
  };
}

if (!customElements.get('rs-load-more')) customElements.define('rs-load-more', RsLoadMore);
